/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs
 * @desc Grid NewIndex Column Definitions
 */

import {
  fieldCAS,
  fieldMolecularFormula,
  fieldMolecularWeight,
  fieldName,
} from 'labstep-web/components/PubChemMetadata/Form/fields';
import { colDefBulkSelect } from 'labstep-web/grid/NewIndex/coldefs/bulk-select';
import { colDefDeviceActionMenu } from 'labstep-web/grid/NewIndex/coldefs/device/action-menu';
import { colDefEntityCreatedAt } from 'labstep-web/grid/NewIndex/coldefs/entity/created-at';
import { colDefEntityEntityUsers } from 'labstep-web/grid/NewIndex/coldefs/entity/entity-users';
import { getColDefEntityName } from 'labstep-web/grid/NewIndex/coldefs/entity/name';
import { colDefEntityPermalink } from 'labstep-web/grid/NewIndex/coldefs/entity/permalink';
import { colDefExperimentWorkflowEndPlannedAt } from 'labstep-web/grid/NewIndex/coldefs/experiment-workflow/end-planned-at';
import { colDefExperimentWorkflowEndedAt } from 'labstep-web/grid/NewIndex/coldefs/experiment-workflow/ended-at';
import { colDefExperimentWorkflowEntityStateUpdatedAt } from 'labstep-web/grid/NewIndex/coldefs/experiment-workflow/entity-state-updated';
import { colDefExperimentWorkflowFolders } from 'labstep-web/grid/NewIndex/coldefs/experiment-workflow/folders';
import { colDefExperimentWorkflowLinkedExperiments } from 'labstep-web/grid/NewIndex/coldefs/experiment-workflow/linked-experiments';
import { colDefExperimentWorkflowStartPlannedAt } from 'labstep-web/grid/NewIndex/coldefs/experiment-workflow/start-planned-at';
import { colDefExperimentWorkflowStartedAt } from 'labstep-web/grid/NewIndex/coldefs/experiment-workflow/started-at';
import { colDefExperimentWorkflowStatus } from 'labstep-web/grid/NewIndex/coldefs/experiment-workflow/status';
import { colDefExperimentWorkflowStatusType } from 'labstep-web/grid/NewIndex/coldefs/experiment-workflow/status-type';
import { colDefExperimentWorkflowTags } from 'labstep-web/grid/NewIndex/coldefs/experiment-workflow/tags';
import { getMetadataColDef } from 'labstep-web/grid/NewIndex/coldefs/metadata';
import { getMetadataMoleculePubchemColDef } from 'labstep-web/grid/NewIndex/coldefs/metadata/molecule/pubchem';
import { getMetadataMoleculeSafetyColDef } from 'labstep-web/grid/NewIndex/coldefs/metadata/molecule/safety';
import { getMetadataMoleculeStructureColDef } from 'labstep-web/grid/NewIndex/coldefs/metadata/molecule/structure';
import { colDefOrderRequestActionMenu } from 'labstep-web/grid/NewIndex/coldefs/order-request/action-menu';
import { colDefOrderRequestAmount } from 'labstep-web/grid/NewIndex/coldefs/order-request/amount';
import { colDefOrderRequestAuthor } from 'labstep-web/grid/NewIndex/coldefs/order-request/author';
import { colDefOrderRequestIsUrgent } from 'labstep-web/grid/NewIndex/coldefs/order-request/is-urgent';
import { getColDefOrderRequestLogAuthor } from 'labstep-web/grid/NewIndex/coldefs/order-request/log-author';
import { getColDefOrderRequestLogCreatedAt } from 'labstep-web/grid/NewIndex/coldefs/order-request/log-created-at';
import { colDefOrderRequestQuantity } from 'labstep-web/grid/NewIndex/coldefs/order-request/quantity';
import { colDefOrderRequestStatus } from 'labstep-web/grid/NewIndex/coldefs/order-request/status';
import { colDefResourceActionMenu } from 'labstep-web/grid/NewIndex/coldefs/resource/action-menu';
import { colDefResourceAvailableItems } from 'labstep-web/grid/NewIndex/coldefs/resource/available-items';
import { colDefResourceItemActionMenu } from 'labstep-web/grid/NewIndex/coldefs/resource-item/action-menu';
import { colDefResourceItemAmount } from 'labstep-web/grid/NewIndex/coldefs/resource-item/amount';
import { colDefResourceItemResource } from 'labstep-web/grid/NewIndex/coldefs/resource-item/resource';
import { colDefResourceItemResourceLocation } from 'labstep-web/grid/NewIndex/coldefs/resource-item/resource-location';
import { colDefResourceItemStatus } from 'labstep-web/grid/NewIndex/coldefs/resource-item/status';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import type { IndexTemplatableEntity } from 'labstep-web/models';
import { Device } from 'labstep-web/models/device.model';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import type { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import {
  OrderRequest,
  OrderRequestStatus,
} from 'labstep-web/models/order-request.model';
import { PurchaseOrder } from 'labstep-web/models/purchase-order.model';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import { colDefOrderRequestPurchaseOrder } from './order-request/purchase-order';
import { colDefResourceCategory } from './resource/category';
import { colDefResourceItemCategory } from './resource-item/category';

export const NEW_INDEX_COL_DEFS: Record<
  string,
  Record<string, NewIndexColDef>
> = {
  [Device.entityName]: {
    action_menu: colDefDeviceActionMenu,
    bulk_select: colDefBulkSelect,
    name: getColDefEntityName(Device.entityName),
    entity_users: colDefEntityEntityUsers,
    created_at: colDefEntityCreatedAt,
    perma_link: colDefEntityPermalink,
  },
  [ExperimentWorkflow.entityName]: {
    action_menu: colDefDeviceActionMenu,
    bulk_select: colDefBulkSelect,
    name: getColDefEntityName(ExperimentWorkflow.entityName),
    entity_users: colDefEntityEntityUsers,
    created_at: colDefEntityCreatedAt,
    perma_link: colDefEntityPermalink,
    status: colDefExperimentWorkflowStatus,
    entity_state_updated:
      colDefExperimentWorkflowEntityStateUpdatedAt,
    started_at: colDefExperimentWorkflowStartedAt,
    ended_at: colDefExperimentWorkflowEndedAt,
    start_planned_at: colDefExperimentWorkflowStartPlannedAt,
    end_planned_at: colDefExperimentWorkflowEndPlannedAt,
    folders: colDefExperimentWorkflowFolders,
    tags: colDefExperimentWorkflowTags,
    linked_experiments: colDefExperimentWorkflowLinkedExperiments,
  },
  [OrderRequest.entityName]: {
    action_menu: colDefOrderRequestActionMenu,
    bulk_select: colDefBulkSelect,
    name: getColDefEntityName(OrderRequest.entityName, false),
    is_urgent: colDefOrderRequestIsUrgent,
    quantity: colDefOrderRequestQuantity,
    status: colDefOrderRequestStatus,
    created_at: colDefEntityCreatedAt,
    author: colDefOrderRequestAuthor,
    order: colDefOrderRequestPurchaseOrder,
    price: colDefOrderRequestAmount,
    perma_link: colDefEntityPermalink,
    approved_log_created_at: getColDefOrderRequestLogCreatedAt(
      OrderRequestStatus.approved,
    ),
    ordered_log_created_at: getColDefOrderRequestLogCreatedAt(
      OrderRequestStatus.ordered,
    ),
    received_log_created_at: getColDefOrderRequestLogCreatedAt(
      OrderRequestStatus.received,
    ),
    approved_log_author: getColDefOrderRequestLogAuthor(
      OrderRequestStatus.approved,
    ),
    ordered_log_author: getColDefOrderRequestLogAuthor(
      OrderRequestStatus.ordered,
    ),
    received_log_author: getColDefOrderRequestLogAuthor(
      OrderRequestStatus.received,
    ),
    entity_users: colDefEntityEntityUsers,
  },
  [Resource.entityName]: {
    action_menu: colDefResourceActionMenu,
    bulk_select: colDefBulkSelect,
    name: getColDefEntityName(Resource.entityName),
    available_items: colDefResourceAvailableItems,
    entity_users: colDefEntityEntityUsers,
    created_at: colDefEntityCreatedAt,
    category: colDefResourceCategory,
    perma_link: colDefEntityPermalink,
  },
  [ResourceItem.entityName]: {
    action_menu: colDefResourceItemActionMenu,
    bulk_select: colDefBulkSelect,
    name: getColDefEntityName(ResourceItem.entityName),
    status: colDefResourceItemStatus,
    amount: colDefResourceItemAmount,
    resource_location: colDefResourceItemResourceLocation,
    resource: colDefResourceItemResource,
    entity_users: colDefEntityEntityUsers,
    created_at: colDefEntityCreatedAt,
    category: colDefResourceItemCategory,
    perma_link: colDefEntityPermalink,
  },
};

export const getMetadataMoleculeColDefs = (
  metadata: Metadata,
): NewIndexColDef[] =>
  metadata.label
    ? [
        getMetadataMoleculeStructureColDef(
          metadata.parent_entity_class,
          metadata.type,
          metadata.label,
        ),
        getMetadataMoleculePubchemColDef(
          metadata.parent_entity_class,
          metadata.type,
          metadata.label,
          'Name',
          fieldName,
        ),
        getMetadataMoleculePubchemColDef(
          metadata.parent_entity_class,
          metadata.type,
          metadata.label,
          'CAS Number',
          fieldCAS,
        ),
        getMetadataMoleculePubchemColDef(
          metadata.parent_entity_class,
          metadata.type,
          metadata.label,
          'Molecular Formula',
          fieldMolecularFormula,
        ),
        getMetadataMoleculePubchemColDef(
          metadata.parent_entity_class,
          metadata.type,
          metadata.label,
          'Molecular Weight',
          fieldMolecularWeight,
        ),
        getMetadataMoleculeSafetyColDef(
          metadata.parent_entity_class,
          metadata.type,
          metadata.label,
        ),
      ]
    : [];

export const getMetadataMoleculeColDef = (
  parentEntityClass: string,
  type: MetadataType,
  metadataLabel: string,
  field: string,
): NewIndexColDef | null => {
  switch (field) {
    case 'structure':
      return getMetadataMoleculeStructureColDef(
        parentEntityClass,
        type,
        metadataLabel,
      );
    case fieldName.name:
      return getMetadataMoleculePubchemColDef(
        parentEntityClass,
        type,
        metadataLabel,
        'Name',
        fieldName,
      );
    case fieldCAS.name:
      return getMetadataMoleculePubchemColDef(
        parentEntityClass,
        type,
        metadataLabel,
        'CAS Number',
        fieldCAS,
      );
    case fieldMolecularFormula.name:
      return getMetadataMoleculePubchemColDef(
        parentEntityClass,
        type,
        metadataLabel,
        'Molecular Formula',
        fieldMolecularFormula,
      );
    case fieldMolecularWeight.name:
      return getMetadataMoleculePubchemColDef(
        parentEntityClass,
        type,
        metadataLabel,
        'Molecular Weight',
        fieldMolecularWeight,
      );
    case 'safety':
      return getMetadataMoleculeSafetyColDef(
        parentEntityClass,
        type,
        metadataLabel,
      );
    default:
      return null;
  }
};

// Configuration for default column layouts per entity type
interface EntityColumnConfig {
  /** Columns to include before metadata columns */
  primaryColumns: string[];
  /** Columns to include after metadata columns */
  secondaryColumns: string[];
  /** Columns to exclude when defaultTemplate exists */
  excludeWithTemplate?: string[];
  /** Premium features required for certain columns */
  premiumRequirements?: Record<string, string>;
  /** Context-specific column exclusions */
  contextExclusions?: Record<string, string[]>;
}

const ENTITY_COLUMN_CONFIGS: Record<string, EntityColumnConfig> = {
  [Device.entityName]: {
    primaryColumns: ['bulk_select', 'name'],
    secondaryColumns: ['entity_users', 'created_at'],
  },
  [ExperimentWorkflow.entityName]: {
    primaryColumns: ['action_menu', 'bulk_select', 'name', 'status'],
    secondaryColumns: [
      'created_at',
      'started_at',
      'ended_at',
      'entity_users',
    ],
  },
  [OrderRequest.entityName]: {
    primaryColumns: [
      'action_menu',
      'bulk_select',
      'name',
      'is_urgent',
      'quantity',
      'status',
      'created_at',
      'author',
      'order',
      'price',
    ],
    secondaryColumns: [
      'approved_log_created_at',
      'ordered_log_created_at',
      'received_log_created_at',
      'approved_log_author',
      'ordered_log_author',
      'received_log_author',
      'entity_users',
    ],
  },
  [Resource.entityName]: {
    primaryColumns: [
      'action_menu',
      'bulk_select',
      'name',
      'available_items',
    ],
    secondaryColumns: ['category', 'entity_users'],
    excludeWithTemplate: ['category'],
  },
  [ResourceItem.entityName]: {
    primaryColumns: [
      'action_menu',
      'bulk_select',
      'name',
      'status',
      'amount',
      'resource_location',
      'resource',
    ],
    secondaryColumns: ['category', 'entity_users'],
    excludeWithTemplate: ['category'],
    contextExclusions: {
      resource_show: ['resource', 'entity_users', 'created_at'],
    },
  },
};

const addMetadataColumns = (
  colDefs: NewIndexColDef[],
  defaultTemplate: IndexTemplatableEntity,
  entityName: string,
): void => {
  // Add resource item template metadata if applicable
  if (
    entityName === 'resource_item' &&
    defaultTemplate.entityName === 'resource' &&
    (defaultTemplate as Resource).resource_item_template
      ?.metadata_thread?.metadatas
  ) {
    const metadatas = (defaultTemplate as Resource)
      .resource_item_template!.metadata_thread.metadatas;
    addMetadataColumnsFromList(colDefs, metadatas, entityName);
  }

  // Add template metadata
  const templateMetadatas = defaultTemplate.metadata_thread.metadatas;
  addMetadataColumnsFromList(colDefs, templateMetadatas, entityName);
};

const addMetadataColumnsFromList = (
  colDefs: NewIndexColDef[],
  metadatas: Metadata[],
  entityName?: string,
): void => {
  for (const metadata of metadatas) {
    if (!metadata.label) {
      continue;
    }

    if (metadata.type === MetadataType.molecule) {
      colDefs.push(
        getMetadataMoleculeStructureColDef(
          metadata.parent_entity_class,
          metadata.type,
          metadata.label,
          entityName,
        ),
        getMetadataMoleculePubchemColDef(
          metadata.parent_entity_class,
          metadata.type,
          metadata.label,
          'Name',
          fieldName,
          entityName,
        ),
        getMetadataMoleculePubchemColDef(
          metadata.parent_entity_class,
          metadata.type,
          metadata.label,
          'CAS Number',
          fieldCAS,
          entityName,
        ),
        getMetadataMoleculePubchemColDef(
          metadata.parent_entity_class,
          metadata.type,
          metadata.label,
          'Molecular Formula',
          fieldMolecularFormula,
          entityName,
        ),
        getMetadataMoleculePubchemColDef(
          metadata.parent_entity_class,
          metadata.type,
          metadata.label,
          'Molecular Weight',
          fieldMolecularWeight,
          entityName,
        ),
        getMetadataMoleculeSafetyColDef(
          metadata.parent_entity_class,
          metadata.type,
          metadata.label,
          entityName,
        ),
      );
    } else {
      colDefs.push(
        getMetadataColDef(
          metadata.parent_entity_class,
          metadata.type,
          metadata.label,
          entityName,
        ),
      );
    }
  }
};

const getColumnDef = (
  entityName: string,
  columnId: string,
): NewIndexColDef | null => {
  return NEW_INDEX_COL_DEFS[entityName]?.[columnId] || null;
};

const filterByPremiumFeatures = (
  colDefs: NewIndexColDef[],
  config: EntityColumnConfig,
  premiumFeatures?: Record<string, boolean>,
): NewIndexColDef[] => {
  if (!config.premiumRequirements || !premiumFeatures) {
    return colDefs;
  }

  return colDefs.filter((colDef) => {
    if (!colDef.colId) {
      return true;
    }
    const requiredFeature = config.premiumRequirements![colDef.colId];
    return !requiredFeature || premiumFeatures[requiredFeature];
  });
};

const filterByContext = (
  colDefs: NewIndexColDef[],
  config: EntityColumnConfig,
  context: string,
): NewIndexColDef[] => {
  const exclusions = config.contextExclusions?.[context];
  if (!exclusions) {
    return colDefs;
  }

  return colDefs.filter(
    (colDef) => !colDef.colId || !exclusions.includes(colDef.colId),
  );
};

export const getDefaultColDefs = (
  context: string,
  entityName: string,
  defaultTemplate?: IndexTemplatableEntity,
  premiumFeatures?: Record<string, boolean>,
): NewIndexColDef[] => {
  const config = ENTITY_COLUMN_CONFIGS[entityName];
  if (!config) {
    // Fallback to original logic for unknown entity types
    return [];
  }

  const colDefs: NewIndexColDef[] = [];

  // Add primary columns
  const primaryColumnsToInclude = config.primaryColumns.filter(
    (columnId) => {
      if (
        config.excludeWithTemplate?.includes(columnId) &&
        defaultTemplate
      ) {
        return false;
      }
      return true;
    },
  );

  for (const columnId of primaryColumnsToInclude) {
    const colDef = getColumnDef(entityName, columnId);
    if (colDef && !colDef.pinned) {
      colDefs.push(colDef);
    }
  }

  // Insert metadata columns at the specified point or at the end of primary columns
  if (defaultTemplate) {
    addMetadataColumns(colDefs, defaultTemplate, entityName);
  }

  // Add secondary columns
  for (const columnId of config.secondaryColumns) {
    const colDef = getColumnDef(entityName, columnId);
    if (colDef) {
      colDefs.push(colDef);
    }
  }

  // Apply filters
  let filteredColDefs = filterByPremiumFeatures(
    colDefs,
    config,
    premiumFeatures,
  );
  filteredColDefs = filterByContext(filteredColDefs, config, context);

  return filteredColDefs;
};

export const getMetadata = (
  parentEntityClass: string,
  entity: any,
  metadataLabel: string,
  metadataType: MetadataType,
) => {
  let entityWithMetadata = entity;
  if (
    parentEntityClass === Resource.entityName &&
    (entity instanceof ResourceItem || entity instanceof OrderRequest)
  ) {
    entityWithMetadata = entity.resource;
  }
  if (
    parentEntityClass === PurchaseOrder.entityName &&
    entity instanceof OrderRequest
  ) {
    entityWithMetadata = entity.purchase_order;
  }

  if (!entityWithMetadata) {
    return null;
  }

  let foundMetadata: Metadata | undefined;

  if (entityWithMetadata.template) {
    const templateMetadata =
      entityWithMetadata.template.metadata_thread.metadatas.find(
        (m: Metadata) =>
          m.type === metadataType && m.label === metadataLabel,
      );
    if (templateMetadata) {
      const matchingMetadata =
        entityWithMetadata.metadata_thread.metadatas.find(
          (m: Metadata) =>
            m.type === metadataType &&
            m.template_id === templateMetadata?.id,
        );
      if (matchingMetadata) {
        foundMetadata = matchingMetadata;
      }
    }
  }

  if (!foundMetadata) {
    foundMetadata = entityWithMetadata.metadata_thread.metadatas.find(
      (m: Metadata) =>
        m.type === metadataType && m.label === metadataLabel,
    );
  }

  if (!foundMetadata) {
    return null;
  }

  // Return lookup source metadata if available, otherwise return the metadata itself
  return foundMetadata.lookup_source || foundMetadata;
};
